import { inject } from "mobx-react";

import { ResponsiveImage } from "../../../../core/components/ResponsiveImage.component";
import { FeedResultFragment, PublishRecordFragment, PwsGuestRecordFragment } from "@ihr-radioedit/inferno-webapi";
import { trimText } from "../../../../core/lib/utilities";
import { isPWSGuestRecord } from "../../../../core/lib/guards";
import { formatImage } from "@ihr-radioedit/inferno-core";
import { isPublishRecord, reverseRoute } from "@inferno/renderer-shared-core";
import { MagicLink } from "../../../../core/ui";
import { CoastFeedItemType, getCoastFeedImg } from "../../lib/coastFeed";
import { RESPONSIVE_IMAGE } from "@inferno/renderer-shared-core";
import type { Store } from "@inferno/renderer-shared-core";

interface CoastFeedArticleItemProps {
  item: FeedResultFragment;
  store?: Store;
}

interface GuestProps {
  imageUrl?: string | null;
  imageAlt: string;
  slug?: string | null;
  name: string;
  bio: string;
}

const getPublishGuestProps = ({ payload, slug }: PublishRecordFragment): GuestProps => {
  const { summary } = payload;

  return {
    imageAlt: summary.image.alt_text || summary.title,
    imageUrl: summary.image.url,
    bio: summary.description,
    name: summary.title,
    slug,
  };
};

const getPWSGuestProps = ({ slug, summary }: PwsGuestRecordFragment): GuestProps => ({
  bio: summary.description,
  imageUrl: summary.image,
  imageAlt: summary.title,
  name: summary.title,
  slug,
});

export const CoastFeedGuestItem = inject("store")(({ item, store }: CoastFeedArticleItemProps) => {
  if (!store) {
    return null;
  }

  let guestProps: GuestProps;

  if (isPublishRecord(item.record)) {
    guestProps = getPublishGuestProps(item.record);
  } else if (isPWSGuestRecord(item.record)) {
    guestProps = getPWSGuestProps(item.record);
  } else {
    return null;
  }

  const guestUrl = reverseRoute(store.site, "guests_detail", { slug: guestProps.slug }) || "#";

  const thumb = formatImage(guestProps.imageUrl || "", store.env.IMAGE_HOST);
  const { imgSrc, srcset } = getCoastFeedImg(thumb, CoastFeedItemType.GUEST, { quality: 80 });

  return (
    <div className="coast-feed-item">
      <figure className="item-thumb">
        <MagicLink to={guestUrl} context={guestProps.slug}>
          <ResponsiveImage
            src={imgSrc}
            srcset={srcset}
            alt={guestProps.imageAlt}
            initialWidth={RESPONSIVE_IMAGE.responsiveImageInitialWidth}
            initialHeight={RESPONSIVE_IMAGE.responsiveImageInitialHeight}
          />
        </MagicLink>
      </figure>

      <MagicLink className="item-title" to={guestUrl} context={guestProps.slug}>
        {guestProps.name}
      </MagicLink>

      <section className="item-summary">{trimText(guestProps.bio, 250)}</section>
    </div>
  );
});

export default CoastFeedGuestItem;
